import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { interviewAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Calendar, TrendingUp, Eye, Plus, Video, PlayCircle } from 'lucide-react';
import { ProgressTracker } from './ProgressTracker';

const TYPE_LABEL = { realtime_voice: 'Voice', structured_4phase: '4-Phase Visa', text_chat: 'Text' };

const InterviewHistory = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const data = await interviewAPI.getHistory();
      setSessions(data.sessions || []);
      setStats(data.stats || null);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load interview history',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const duration = (s) => {
    if (!s.completed_at || !s.created_at) return 'In progress';
    return `${Math.max(1, Math.round((new Date(s.completed_at) - new Date(s.created_at)) / 60000))} minutes`;
  };

  const continueSession = (s) => {
    if (s.type === 'structured_4phase') return navigate('/interview/structured');
    if (s.type === 'realtime_voice') return navigate('/interview/realtime');
    navigate(`/interview/session/${s.session_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="text-xl text-gray-700">Loading interview history...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-8" data-testid="interview-history-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
            Interview Replays
          </h1>
          <p className="text-gray-600">Replay past sessions, revisit coach feedback and track your improvement</p>
        </div>

        <ProgressTracker stats={stats} />

        <Card className="mb-8 bg-gradient-to-br from-white to-emerald-50 border-2 border-emerald-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold mb-2">Ready for another practice session?</h3>
                <p className="text-gray-600">Start a new interview to continue improving</p>
              </div>
              <Button
                onClick={() => navigate('/interview')}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                data-testid="new-interview-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Interview
              </Button>
            </div>
          </CardContent>
        </Card>

        {sessions.length === 0 ? (
          <Card data-testid="history-empty-state">
            <CardContent className="p-12 text-center">
              <Video className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No interviews yet</h3>
              <p className="text-gray-600 mb-6">Start your first AI interview practice session</p>
              <Button
                onClick={() => navigate('/interview')}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              >
                Start Interview
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="history-session-list">
            {sessions.map((session) => (
              <Card key={session.session_id} className="hover:shadow-lg transition-shadow" data-testid={`history-session-${session.session_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-3 flex-wrap gap-y-2">
                    <Badge className={getStatusColor(session.status)}>
                      {(session.status || 'unknown').replace('_', ' ').toUpperCase()}
                    </Badge>
                    <Badge variant="outline">{TYPE_LABEL[session.type] || session.type}</Badge>
                    {typeof session.score === 'number' && (
                      <div className="flex items-center">
                        <TrendingUp className="h-4 w-4 mr-1 text-gray-500" />
                        <span className={`font-semibold ${getScoreColor(session.score)}`}>{session.score}/100</span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-gray-600">Date</p>
                      <p className="font-semibold flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-gray-500" />
                        {session.created_at ? new Date(session.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Duration</p>
                      <p className="font-semibold">{duration(session)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Questions</p>
                      <p className="font-semibold">{session.question_count}</p>
                    </div>
                  </div>

                  {session.summary && <p className="text-gray-600 text-sm line-clamp-2 mb-4">{session.summary}</p>}

                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/interview/replay/${session.session_id}`)}
                      size="sm"
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                      disabled={!session.question_count}
                      data-testid={`replay-btn-${session.session_id}`}
                    >
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Replay
                    </Button>
                    {session.status === 'completed' && session.type !== 'structured_4phase' ? (
                      <Button onClick={() => navigate(`/interview/feedback/${session.session_id}`)} variant="outline" size="sm" data-testid={`feedback-btn-${session.session_id}`}>
                        <Eye className="h-4 w-4 mr-2" />
                        Feedback
                      </Button>
                    ) : session.status !== 'completed' ? (
                      <Button onClick={() => continueSession(session)} variant="outline" size="sm" data-testid={`continue-btn-${session.session_id}`}>
                        Continue
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewHistory;
